import React from 'react';
import { getStageLabel, fmtDur } from '../../platform/store/useOperationalStore';
import { fmtTime, fmtAgo, parseLegacyText } from './roHelpers';
import LifecycleRail from './LifecycleRail';

function fmtHeaderMoney(n) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export default function RoHeader({
  truck, stageAccent, currentBlocker, isClosed, stageMs, totalMs, now,
  hasPromise, promiseMs, promiseOverdue, promiseRemaining,
  primaryAction, secondaryActions, advancingStage, canMove, canClose, canVoid,
  confirmComplete, setConfirmComplete, confirmVoid, setConfirmVoid, currentRole,
  onOpenEstimate, onOpenInvoice, onBack,
  advanceStage, handleComplete, handleVoid, handlePrint, handleShare,
  editingPromise, setEditingPromise, promiseInput, setPromiseInput, handleSavePromise, onClearPromise,
  techs, setAssignTechOpen, canEdit, lastActivity,
  approvedAmount, unbilledHours, moneyAction, settings,
}) {
  return (
    <>
      {/* ── STICKY HEADER ─────────────────────────────────────────────────── */}
      <header className="ro-header" style={{ '--stage-accent': stageAccent }}>
        <div className="ro-header-left">
          <button className="ro-back-btn" onClick={onBack} title="Back">
            ← Back
          </button>
          <div className="ro-header-id">
            <span className="ro-ro-number">{truck.repairOrder}</span>
            <span className="ro-stage-badge" style={{ background: stageAccent + '22', color: stageAccent, border: `1px solid ${stageAccent}55` }}>
              {getStageLabel(truck.status)}
            </span>
            {currentBlocker && <span className="ro-blocked-badge">⛔ Blocked</span>}
            {isClosed && <span className={`ro-closed-badge ro-closed-badge--${truck.status}`}>{truck.status}</span>}
          </div>
          <div className="ro-header-age">
            {fmtDur(stageMs)} in stage · {fmtDur(totalMs)} total
          </div>
          {(approvedAmount != null || unbilledHours != null) && (
            <div className="ro-header-money">
              {approvedAmount != null && (
                <span className="ro-header-money-chip ro-header-money-chip--approved">
                  {fmtHeaderMoney(approvedAmount)} approved
                </span>
              )}
              {unbilledHours != null && (
                <span className="ro-header-money-chip ro-header-money-chip--unbilled">
                  {unbilledHours.toFixed(1)}h unbilled
                </span>
              )}
            </div>
          )}
        </div>

        <div className="ro-header-center">
          {hasPromise ? (
            <div className={`ro-promise-display ${promiseOverdue ? 'overdue' : ''}`}>
              <span className="ro-promise-label">Promised</span>
              <span className="ro-promise-time">{fmtTime(truck.promisedTime)}</span>
              <span className="ro-promise-countdown">
                {promiseOverdue
                  ? `OVERDUE ${fmtDur(Math.abs(promiseRemaining))}`
                  : `in ${fmtDur(promiseRemaining)}`}
              </span>
            </div>
          ) : (
            <div className="ro-promise-display ro-promise-empty">
              <span className="ro-promise-label">No Promise Set</span>
              {canEdit && !isClosed && (
                <button className="ro-promise-set-btn" onClick={() => {
                  const d = new Date(Date.now() + 4 * 3_600_000);
                  setPromiseInput(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
                  setEditingPromise(true);
                }}>+ Set</button>
              )}
            </div>
          )}
        </div>

        <div className="ro-header-right">
          {!isClosed && (moneyAction || primaryAction) && (
            <div className="ro-action-group">
              {secondaryActions.map(sa => (
                <button key={sa.nextStatus} className="ro-btn ro-btn--secondary"
                  onClick={() => advanceStage(sa.nextStatus)}
                  disabled={advancingStage || !canMove}>
                  {sa.label}
                </button>
              ))}
              {moneyAction ? (
                <button className="ro-btn ro-btn--primary ro-btn--money"
                  onClick={() => moneyAction.kind === 'workflow'
                    ? advanceStage(moneyAction.nextStatus)
                    : (/invoice/i.test(moneyAction.label) ? onOpenInvoice() : onOpenEstimate())}
                  disabled={moneyAction.kind === 'workflow' && (advancingStage || !canMove)}
                  style={{ background: stageAccent }}
                  title="Next step in the money loop">
                  $ {moneyAction.label}
                </button>
              ) : primaryAction.action === 'complete' ? (
                confirmComplete ? (
                  <>
                    <button className="ro-btn ro-btn--confirm" onClick={handleComplete}>Confirm Close</button>
                    <button className="ro-btn ro-btn--cancel" onClick={() => setConfirmComplete(false)}>Cancel</button>
                  </>
                ) : (
                  <button className="ro-btn ro-btn--primary" onClick={() => setConfirmComplete(true)} disabled={!canClose}
                    title={!canClose ? `Closing an RO requires a manager or admin role (you are signed in as ${currentRole})` : undefined}>
                    {primaryAction.label}{!canClose ? ' 🔒' : ''}
                  </button>
                )
              ) : (
                <button className="ro-btn ro-btn--primary"
                  onClick={() => advanceStage(primaryAction.nextStatus)}
                  disabled={advancingStage || !canMove}
                  style={{ background: stageAccent }}>
                  {advancingStage ? '…' : primaryAction.label}
                </button>
              )}
            </div>
          )}

          <div className="ro-header-tools">
            <button className="ro-tool-btn ro-tool-btn--invoice" onClick={onOpenInvoice} title="Open the invoice">Invoice</button>
            <button className="ro-tool-btn" onClick={handlePrint} title="Print">⎙</button>
            <button className="ro-tool-btn" onClick={handleShare} title="Copy link">⎘</button>
            {!isClosed && (
              canVoid ? (
                confirmVoid ? (
                  <>
                    <button className="ro-tool-btn ro-tool-btn--danger" onClick={handleVoid}>Void?</button>
                    <button className="ro-tool-btn" onClick={() => setConfirmVoid(false)}>✕</button>
                  </>
                ) : (
                  <button className="ro-tool-btn" onClick={() => setConfirmVoid(true)} title="Void RO">⊘</button>
                )
              ) : (
                <button className="ro-tool-btn" disabled
                  title={`Voiding an RO requires a manager or admin role (you are signed in as ${currentRole})`}>⊘</button>
              )
            )}
          </div>
        </div>
      </header>

      {/* ── GLANCE LAYER ──────────────────────────────────────────────────── */}
      <section className="ro-glance">
        <div className="ro-complaint-block">
          <div className="ro-complaint-eyebrow">COMPLAINT</div>
          <div className="ro-complaint-text">{truck.reason || truck.faults || 'No complaint recorded'}</div>
          <div className="ro-unit-line">
            <span className="ro-unit-id">{[truck.year, truck.make, truck.model].filter(Boolean).join(' ') || '—'}</span>
            {truck.truckNumber && <span className="ro-unit-num">Unit {truck.truckNumber}</span>}
            {truck.vin && <span className="ro-unit-vin">VIN: {truck.vin}</span>}
            <span className="ro-company">{truck.companyName}</span>
          </div>
        </div>

        <div className="ro-glance-meta">
          <div className="ro-glance-techs">
            {techs.length === 0
              ? <button className="ro-assign-btn" onClick={() => canEdit && setAssignTechOpen(true)}>+ Assign Tech</button>
              : techs.map(n => (
                  <button key={n} className="ro-tech-avatar" title={`Tech #${n}`}
                    onClick={() => canEdit && setAssignTechOpen(true)}>
                    {n}
                  </button>
                ))
            }
          </div>
          <LifecycleRail truck={truck} settings={settings} />
          {lastActivity && (
            <div className="ro-last-act">
              <span className="ro-last-act-author">{lastActivity.author || '—'}</span>
              <span className="ro-last-act-sep">·</span>
              <span className="ro-last-act-time">{fmtAgo(lastActivity.timestamp, now)} ago</span>
              <span className="ro-last-act-sep">·</span>
              <span className="ro-last-act-text">
                {parseLegacyText(lastActivity.text).text?.slice(0, 60) || lastActivity.type}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ── PROMISE EDITOR (inline) ────────────────────────────────────────── */}
      {editingPromise && (
        <div className="ro-promise-editor">
          <input type="datetime-local" className="ro-promise-input" value={promiseInput}
            onChange={e => setPromiseInput(e.target.value)} />
          <button className="ro-btn ro-btn--primary" onClick={handleSavePromise}>Save</button>
          {truck.promisedTime && (
            <button className="ro-btn ro-btn--ghost" onClick={onClearPromise}>Clear</button>
          )}
          <button className="ro-btn ro-btn--ghost" onClick={() => setEditingPromise(false)}>Cancel</button>
        </div>
      )}
    </>
  );
}
